import {
  Box,
  Divider,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import deleteNoteImg from "../../../assets/delete-note.svg";
import dotsHorizontal from "../../../assets/dots-horizontal.svg";
import editNoteImg from "../../../assets/edit-note.svg";
import { useAuth } from "../../../context/AuthContext";
import {
  createNote,
  undoDeleteNote,
  updateNote,
} from "../../../services/api/apiClient";
import { brand, error, gray } from "../../../theme/themePrimitives";
import { format } from "date-fns";
import {
  CreateUpdateNoteBody,
  UserAssignmentNote,
} from "../../../services/api/types/general/homeAssignment";
import { usePortal } from "../../../context/PortalsContext";
import { REMOVETEXT } from "../../../utils/constants";
import useScreenSize from "../../../hooks/useScreenSize";

interface NotesProps {
  notes: UserAssignmentNote[];
  setNotes: React.Dispatch<React.SetStateAction<UserAssignmentNote[]>>;
  assignmentId: string;
}

export const Notes: React.FC<NotesProps> = ({
  notes,
  setNotes,
  assignmentId,
}) => {
  const [note, setNote] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedNoteIndex, setSelectedNoteIndex] = useState<string | null>(
    null,
  );
  const [editedNoteIds, setEditedNoteIds] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const { auth } = useAuth();
  const { portals } = usePortal();
  const { isTablet } = useScreenSize();

  useEffect(() => {
    const edited = new Set(
      notes
        .filter((note) => note.IsEdited)
        .map((note) => note.UserAssignmentNoteUid),
    );
    setEditedNoteIds(edited);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter" && note.trim() !== "") {
      if (
        !auth.user?.UserPortalUid ||
        !assignmentId ||
        !portals.selectedPortal
      ) {
        return;
      }
      if (isEditing && selectedNoteIndex) {
        const body: CreateUpdateNoteBody = {
          isDeleted: false,
          note: note,
        };
        const response = await updateNote(
          portals.selectedPortal.portalUid,
          auth.user?.UserPortalUid,
          assignmentId,
          selectedNoteIndex,
          body,
        );

        const updatedNotes = [...notes];
        const noteIndex = updatedNotes.findIndex(
          (n) => n.UserAssignmentNoteUid === selectedNoteIndex,
        );
        if (noteIndex !== -1) {
          updatedNotes[noteIndex] = {
            ...updatedNotes[noteIndex],
            Note: note,
            Datetime: response.Datetime.toString(),
          };
          setNotes(updatedNotes);
        }
        setEditedNoteIds((prev) => new Set(prev).add(selectedNoteIndex));
        setIsEditing(false);
        setSelectedNoteIndex(null);
        setNote("");
      } else {
        const body: CreateUpdateNoteBody = {
          isDeleted: false,
          note: note,
        };
        const response = await createNote(
          portals.selectedPortal.portalUid,
          auth.user?.UserPortalUid,
          assignmentId,
          body,
        );
        setNotes([
          ...notes,
          {
            UserAssignmentNoteUid: response.UserAssignmentNoteUid,
            Note: note,
            IsMarkedForDelete: false,
            IsEdited: false,
            Datetime: response.Datetime.toString(),
          },
        ]);
        setNote("");
      }
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    index: string,
  ) => {
    setMenuAnchor(event.currentTarget);
    setSelectedNoteIndex(index);
  };

  const handleMenuClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setMenuAnchor(null);
  };

  const handleDelete = async () => {
    if (selectedNoteIndex !== null && portals.selectedPortal && auth.user) {
      setNotes((prevNotes) =>
        prevNotes.map((note) =>
          note.UserAssignmentNoteUid === selectedNoteIndex
            ? {
                ...note,
                Note: `Note ${REMOVETEXT}d`,
                IsMarkedForDelete: true,
                Datetime: new Date().toISOString(),
              }
            : note,
        ),
      );
      handleMenuClose();
      const body: CreateUpdateNoteBody = {
        isDeleted: true,
        note: note,
      };
      await updateNote(
        portals.selectedPortal.portalUid,
        auth.user?.UserPortalUid,
        assignmentId,
        selectedNoteIndex,
        body,
      );
    }
    setSelectedNoteIndex(null);
  };

  const handleUndoDelete = async (noteId: string) => {
    if (auth.user && portals.selectedPortal) {
      const response = await undoDeleteNote(
        portals.selectedPortal.portalUid,
        auth.user?.UserPortalUid,
        assignmentId,
        noteId,
      );
      setNotes((prevNotes) =>
        prevNotes.map((note) =>
          note.UserAssignmentNoteUid === noteId
            ? {
                ...note,
                Note: response.note,
                IsMarkedForDelete: false,
              }
            : note,
        ),
      );
    }
  };

  const handleEdit = () => {
    if (selectedNoteIndex !== null) {
      const note = notes.find(
        (n: UserAssignmentNote) =>
          n.UserAssignmentNoteUid === selectedNoteIndex,
      )?.Note;
      if (note) {
        setNote(note);
        setIsEditing(true);
      }
    }
    handleMenuClose();
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: isTablet ? "calc(100vh - 105px)" : "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        padding: "10px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          marginTop: "1rem",
          marginBottom: "0.5rem",
          paddingRight: "10px",
        }}
      >
        {notes.map((note) => {
          const isEdited = editedNoteIds.has(note.UserAssignmentNoteUid);
          const isDeleted = note.IsMarkedForDelete;
          const dateFormatted = format(new Date(note.Datetime), "dd.MM.yyyy");

          return (
            <Box key={note.UserAssignmentNoteUid} sx={{ marginBottom: "1rem" }}>
              {!isDeleted && !isEdited && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mb: 0.5, ml: "4px" }}
                >
                  {dateFormatted}
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  backgroundColor: gray[100],
                  borderRadius: "8px",
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    wordWrap: "break-word",
                    whiteSpace: "pre-wrap",
                    color: isDeleted ? gray[500] : "inherit",
                  }}
                >
                  {isDeleted ? `Note ${REMOVETEXT}d` : note.Note}
                </Typography>

                {!isDeleted ? (
                  <img
                    src={dotsHorizontal}
                    alt="options"
                    style={{ width: 20, height: 20, cursor: "pointer" }}
                    onClick={(e) =>
                      handleMenuOpen(e, note.UserAssignmentNoteUid)
                    }
                  />
                ) : (
                  <Typography
                    onClick={() => handleUndoDelete(note.UserAssignmentNoteUid)}
                    sx={{
                      cursor: "pointer",
                      color: brand[600],
                    }}
                  >
                    Undo
                  </Typography>
                )}
              </Box>
              {(isDeleted || isEdited) && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mt={1}
                >
                  <Typography variant="caption" color="textSecondary">
                    {isDeleted
                      ? "This note will disappear in 1 hr"
                      : "Note edited"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {dateFormatted}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() => handleEdit()}
          sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
        >
          <img src={editNoteImg} alt="edit" />
          <Typography>Edit Note</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            color: error[500],
          }}
        >
          <img src={deleteNoteImg} alt={REMOVETEXT} />
          <Typography>{REMOVETEXT} Note</Typography>
        </MenuItem>
      </Menu>

      <Divider sx={{ margin: "0 -10px 0.5rem" }} />
      <TextField
        size="small"
        inputRef={inputRef}
        fullWidth
        label="Enter notes"
        variant="outlined"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    </Box>
  );
};
